import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { backBtnStyle, sharedStyles } from "@lib/sharedStyles";

// Ensure custom elements are registered
import "@/components/button/BasicButton";
import "@/components/inputs-textarea/BasicInput";
import "@/components/inputs-textarea/BasicNumberInput";
import "@/components/select/BasicSelect";

interface Option { id: string; name: string }
interface QuestionOption { id: string; question: string }

const API_BASE = (window.location.port === '5173') ? 'http://localhost:8002' : '';

async function parseJsonOrThrow(res: Response) {
  // Check for HTTP OK first
  if (!res.ok) {
    // Try to extract body for debugging
    let bodyText = '';
    try { bodyText = await res.text(); } catch (e) { bodyText = '<unreadable body>'; }
    throw new Error(`HTTP ${res.status}: ${bodyText.substring(0, 300)}`);
  }
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) {
    const text = await res.text();
    throw new Error(`Expected application/json but got '${ct}'. Response body starts with: ${text.substring(0, 300)}`);
  }
  return res.json();
}

function navigate(path: string) {
  const state = {};
  window.history.pushState(state, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

@customElement('quiz-create-page')
export default class QuizCreatePage extends LitElement {
  static styles = [sharedStyles, backBtnStyle, css`
    :host { display: block; padding: 1rem; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; align-items: start; }
    .card { border: 1px solid #ddd; border-radius: 8px; padding: 1rem; background: #fff; }
    .row { display: grid; gap: 0.5rem; }
    label { font-weight: 600; }
    input, select, textarea { padding: 0.5rem; border-radius: 6px; border: 1px solid #ccc; width: 100%; box-sizing: border-box; }
    .list { max-height: 280px; overflow: auto; border: 1px solid #eee; padding: 0.5rem; border-radius: 6px; }
    .actions { display: flex; gap: 0.5rem; }
    button { padding: 0.5rem 0.9rem; border-radius: 6px; border: 1px solid #ccc; cursor: pointer; }
    .error { color: #b00020; }
    .muted { color: #666; font-size: 0.9rem; }
  `];

  @property({ type: String }) quizId!: string;

  @state() private loading = true;
  @state() private saving = false;
  @state() private error: string | null = null;

  @state() private name: string = '';
  @state() private quizTypeId: string | null = null;

  @state() private allQuizTypes: Option[] = [];
  @state() private allCategories: Option[] = [];
  @state() private allDifficulties: number[] = [];

  @state() private selectedCategoryIds: Set<string> = new Set();
  @state() private selectedDifficultyIds: Set<number> = new Set();

  @state() private catLimitMsg: string | null = null;
  @state() private diffLimitMsg: string | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.loadAll();
  }

  private async loadAll() {
    this.loading = true;
    this.error = null;

    try {
      const [catsRes, diffsRes, typesRes] = await Promise.all([
        fetch(`${API_BASE}/api/jeopardy/categories`),
        fetch(`${API_BASE}/api/jeopardy/difficulties`),
        fetch(`${API_BASE}/api/quiz-types`),
      ]);

      const cats = await parseJsonOrThrow(catsRes);
      const diffs = await parseJsonOrThrow(diffsRes);
      const types = await parseJsonOrThrow(typesRes);

      this.allCategories = (cats.results ?? []) as Option[];
      this.allDifficulties = (diffs.results ?? []) as number[];
      this.allQuizTypes = (types.results ?? []) as Option[];

      // Try load existing quiz
      const quizRes = await fetch(`${API_BASE}/api/quizzes/${encodeURIComponent(this.quizId)}`);
      if (quizRes.status === 404) {
      } else {
        const q = await parseJsonOrThrow(quizRes);
        this.name = q.name ?? '';
        this.quizTypeId = q.quiz_type?.id ?? null;
        this.selectedCategoryIds = new Set((q.categories ?? []).map((i: any) => i.id));
        this.selectedDifficultyIds = new Set((q.difficulties ?? []) as number[]);
      }
    } catch (e: any) {
      this.error = e?.message ?? 'Failed to load data';
    } finally {
      this.loading = false;
    }
  }

  private toggleCategory = (id: string) => {
    const next = new Set(this.selectedCategoryIds);
    if (next.has(id)) {
      next.delete(id);
      this.catLimitMsg = null;
    } else {
      if (next.size >= 5) {
        this.catLimitMsg = 'You can select at most 5 categories';
        return;
      }
      next.add(id);
      this.catLimitMsg = null;
    }
    this.selectedCategoryIds = next;
  };

  private toggleDifficulty = (value: number) => {
    const next = new Set(this.selectedDifficultyIds);
    if (next.has(value)) {
      next.delete(value);
      this.diffLimitMsg = null;
    } else {
      if (next.size >= 5) {
        this.diffLimitMsg = 'You can select at most 5 difficulties';
        return;
      }
      next.add(value);
      this.diffLimitMsg = null;
    }
    this.selectedDifficultyIds = next;
  };

  private save = async () => {
    this.saving = true;
    this.error = null;
    try {
      const payload = {
        name: this.name,
        quiz_type_id: this.quizTypeId,
        category_ids: Array.from(this.selectedCategoryIds),
        difficulty_ids: Array.from(this.selectedDifficultyIds),
      };
      const res = await fetch(`${API_BASE}/api/quizzes/${encodeURIComponent(this.quizId)}/upsert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      const saved = await res.json();
      this.name = saved.name ?? this.name;
      // nothing else to do; sets are already what we posted
      // Navigate back to My Quizzes
      // const state = {}; window.history.pushState(state, '', '/quizzes'); window.dispatchEvent(new PopStateEvent('popstate'));
    } catch (e: any) {
      this.error = e?.message ?? 'Save failed';
    } finally {
      this.saving = false;
    }
  };

  // Generic input-change handler for simple fields like name
  private onInputChange(e: any) {
    const name = e.detail.name as string;
    const value = e.detail.value;
    if (name === 'name') this.name = value;
  }

  private onQuizTypeSelection(e: any) {
    const newValue = e.detail.newValue;
    this.quizTypeId = newValue || null;
  }

  private renderList(items: Option[] | QuestionOption[], selected: Set<string>, onToggle: (id: string) => void, labelKey: 'name' | 'question' = 'name') {
    return html`<div class="list">
      ${items.map((i: any) => html`
        <label style="display:flex; align-items:center; gap:0.5rem; padding: 0.25rem 0;">
          <input type="checkbox" .checked=${selected.has(i.id)} @change=${() => onToggle(i.id)} />
          <span>${i[labelKey]}</span>
        </label>
      `)}
    </div>`;
  }

  render() {
    if (this.loading) return html`<div>Loading...</div>`;
    return html`
      <div style="display:flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">
        <div>
          <h2>Create/Edit Quiz</h2>
          <div class="muted">ID: ${this.quizId}</div>
        </div>
        <basic-button @click=${() => { const state = {}; window.history.pushState(state, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')); }}>Back to Main Menu</basic-button>
      </div>
      ${this.error ? html`<div class="error">${this.error}</div>` : nothing}
      <div class="grid">
        <div class="card">
          <div class="row">
            <basic-input id="name" name="name" .label=${'Name'} .value=${this.name} @input-change=${(e: any) => this.onInputChange(e)}></basic-input>
          </div>
          <div class="row">
            <label for="type">Quiz Type</label>
            <basic-select .value=${this.quizTypeId ?? ''} .options=${this.allQuizTypes.map(t => ({ value: t.id, label: t.name }))} .additionalCustomEventIdentifier=${'quiz_type'} @selection-change=${(e: any) => this.onQuizTypeSelection(e)}></basic-select>
          </div>
          <div class="actions" style="margin-top: 0.75rem;">
            <basic-button @click=${this.save} ?disabled=${this.saving || !this.quizTypeId}>${this.saving ? 'Saving…' : 'Save'}</basic-button>
          </div>
        </div>
        <div class="card">
          <h3>Categories</h3>
          <div class="muted">Selected ${this.selectedCategoryIds.size} / 5 ${this.catLimitMsg ? html`<span class="error">- ${this.catLimitMsg}</span>` : nothing}</div>
          ${this.renderList(this.allCategories, this.selectedCategoryIds, (id) => this.toggleCategory(id))}
        </div>
        <div class="card">
          <h3>Difficulties</h3>
          <div class="muted">Selected ${this.selectedDifficultyIds.size} / 5 ${this.diffLimitMsg ? html`<span class="error">- ${this.diffLimitMsg}</span>` : nothing}</div>
          <div class="list">
            ${this.allDifficulties.map(d => html`
              <label style="display:flex; align-items:center; gap:0.5rem; padding: 0.25rem 0;">
                <input type="checkbox" .checked=${this.selectedDifficultyIds.has(d)} @change=${() => this.toggleDifficulty(d)} />
                <span>${d}</span>
              </label>
            `)}
          </div>
        </div>
      </div>
    `;
  }
}
